import { useAutoneTranslation } from '@autone/translations';
import { GripVertical, Pin, Search, X } from 'lucide-react';
import { AnimatePresence, Reorder } from 'motion/react';
import { type HTMLAttributes, useEffect, useRef, useState } from 'react';

import { Checkbox, cn } from '../../../tailwind';
import { InputWithIcon } from '../../../tailwind/inputs';
import { RippleRoot } from '../../../tailwind/RippleRoot';

import { useColumnVisibilityMenuContext } from './ColumnVisibilityMenu.context';
import { type Column, type ColumnGroup } from './ColumnVisibilityMenu.types';

const ColumnVisibilityMenuPanelContainer = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn('flex flex-col gap-8 p-4 min-h-0 h-full w-full', className)}
      {...props}
    >
      {children}
    </div>
  );
};

export const ColumnVisibilityMenuOptionsPanel = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  const { common } = useAutoneTranslation();

  const [searchTerm, setSearchTerm] = useState('');
  const { columnOptions, maxAdditionalColumnsReached } =
    useColumnVisibilityMenuContext();

  const filteredColumnGroups = columnOptions
    .map((columnGroup) => {
      const filteredColumns = columnGroup.columns.filter((column) =>
        column.translatedDisplayName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );

      return {
        ...columnGroup,
        columns: filteredColumns,
      };
    })
    .filter((columnGroup) => columnGroup.columns.length > 0);

  const hasResults = filteredColumnGroups.length > 0;

  return (
    <ColumnVisibilityMenuPanelContainer className={className} {...props}>
      <div className="flex flex-col gap-1.5 h-fit w-full">
        <div className="flex flex-row justify-between items-center gap-2">
          <p className="text-lg font-bold">
            {common('column-visibility-menu.columns')}
          </p>
          {maxAdditionalColumnsReached && (
            <p className="px-2 py-1 rounded-md bg-red-10 font-bold text-red-120">
              {common('column-visibility-menu.selected-columns-limit-reached')}
            </p>
          )}
        </div>
        <InputWithIcon
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search size={18} className="text-grey-60" />}
          className="h-10 w-full"
          placeholder={common('search-placeholder')}
        />
      </div>
      {hasResults ? (
        <div className="grow w-full overflow-y-scroll no-scrollbar">
          <ul className="flex flex-col gap-6">
            {filteredColumnGroups.map((columnGroup) => (
              <ColumnVisibilityMenuColumnGroup
                key={columnGroup.id}
                columnGroup={columnGroup}
              />
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col items-center p-2">
          <p className="text-base font-medium text-grey-60">
            {common('no-results')}
          </p>
        </div>
      )}
    </ColumnVisibilityMenuPanelContainer>
  );
};

interface ColumnGroupProps {
  columnGroup: ColumnGroup;
}

const ColumnVisibilityMenuColumnGroup = ({ columnGroup }: ColumnGroupProps) => {
  return (
    <li className="flex flex-col gap-4">
      <p className="text-sm text-grey-80 capitalize">
        {columnGroup.translatedDisplayName}
      </p>
      <ul className="flex flex-col">
        {columnGroup.columns.map((column) => (
          <ColumnVisibilityMenuColumnOption key={column.id} column={column} />
        ))}
      </ul>
    </li>
  );
};

interface ColumnVisibilityMenuColumnOptionProps {
  column: Column;
}

const ColumnVisibilityMenuColumnOption = ({
  column,
}: ColumnVisibilityMenuColumnOptionProps) => {
  const {
    columns,
    onColumnsChange,
    maxAdditionalColumnsReached,
    columnPinning,
  } = useColumnVisibilityMenuContext();
  const isPinnedColumn =
    columnPinning.leftPinned?.includes(column.id) ||
    columnPinning.rightPinned?.includes(column.id);
  const columnIsSelected = !!columns.find(
    (selectedColumnId) => selectedColumnId === column.id,
  );
  const isDisabled = maxAdditionalColumnsReached && !columnIsSelected;

  const handleToggleColumnOption = () => {
    if (!columnIsSelected && !maxAdditionalColumnsReached) {
      onColumnsChange([...columns, column.id]);
    } else {
      onColumnsChange(
        columns.filter((selectedColumnId) => selectedColumnId !== column.id),
      );
    }
  };

  return (
    <li
      key={column.id}
      className={cn(
        'relative overflow-hidden flex flex-row items-center gap-2 hover:bg-grey-1 hover:cursor-pointer rounded-md',
        isDisabled && 'hover:cursor-not-allowed',
      )}
      onClick={handleToggleColumnOption}
    >
      {!isDisabled && (
        <RippleRoot
          className="bg-grey-40"
          pulseDuration={0.55}
          pulseOpacity={0.3}
        />
      )}
      <div
        className={cn(
          'grid place-items-center aspect-square p-3 rounded-full',
          !isDisabled && 'hover:bg-primary-10',
        )}
      >
        <Checkbox checked={columnIsSelected} disabled={isDisabled} />
      </div>
      <label
        className={cn(
          'text-sm hover:cursor-pointer',
          isDisabled && 'text-grey-40 hover:cursor-not-allowed',
        )}
      >
        {column.translatedDisplayName}
      </label>
      {isPinnedColumn && (
        <Pin
          size={18}
          className="ms-auto me-2 fill-grey-60 text-grey-60 rotate-45"
        />
      )}
    </li>
  );
};

export const ColumnVisibilityMenuReorderPanel = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  const { common } = useAutoneTranslation();

  const { columns, permanentColumns, maxAdditionalColumns } =
    useColumnVisibilityMenuContext();
  const additionalColumnsSelected = columns.length - permanentColumns.length;

  const additionalColumnsSelectedText = common(
    'column-visibility-menu.selected-columns-subtitle',
    {
      activeColumnsCount: additionalColumnsSelected,
      columnLimit: maxAdditionalColumns,
    },
  );

  return (
    <ColumnVisibilityMenuPanelContainer className={className} {...props}>
      <div className="flex flex-col gap-1 w-full">
        <p className="text-lg font-bold">
          {common('column-visibility-menu.selected-columns')}
        </p>
        <p>{additionalColumnsSelectedText}</p>
      </div>
      <ColumnVisibilityMenuReorderItems />
    </ColumnVisibilityMenuPanelContainer>
  );
};

const ColumnVisibilityMenuReorderItems = () => {
  const { columns, onColumnsChange } = useColumnVisibilityMenuContext();

  return (
    <div className="overflow-y-scroll no-scrollbar grow">
      <Reorder.Group
        data-testid="column-visibility-menu-reorder-items-group"
        className={'flex flex-col gap-2'}
        values={columns}
        axis="y"
        onReorder={onColumnsChange}
      >
        <AnimatePresence>
          {columns.map((column) => (
            <ColumnVisibilityMenuReorderItem key={column} column={column} />
          ))}
        </AnimatePresence>
      </Reorder.Group>
    </div>
  );
};

interface ColumnVisibilityMenuReorderItemProps {
  column: string;
}

const ColumnVisibilityMenuReorderItem = ({
  column,
}: ColumnVisibilityMenuReorderItemProps) => {
  const {
    flattenedColumnOptions,
    permanentColumnsSet,
    columns,
    columnPinning,
    onColumnsChange,
  } = useColumnVisibilityMenuContext();

  const isPermanentColumn = permanentColumnsSet.has(column);
  const isPinnedColumn =
    columnPinning.leftPinned?.includes(column) ||
    columnPinning.rightPinned?.includes(column);
  const isReorderable = !isPermanentColumn && !isPinnedColumn;

  const ref = useRef<HTMLLIElement>(null);

  const handleRemoveColumn = () => {
    onColumnsChange(
      columns.filter((selectedColumnId) => selectedColumnId !== column),
    );
  };

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <Reorder.Item
      role={'list-item'}
      data-testid={isPinnedColumn && 'pinned-column'}
      initial={{
        left: isPermanentColumn ? 0 : '-100%',
        opacity: isPermanentColumn ? 1 : 0,
      }}
      animate={{
        left: 0,
        opacity: 1,
      }}
      exit={{
        left: '-100%',
        opacity: 0,
      }}
      ref={ref}
      key={column}
      value={column}
      dragListener={isReorderable}
      className={cn(
        'relative overflow-hidden bg-grey-10 text-grey-80 font-medium flex flex-row items-center gap-2 justify-between rounded-md p-2 shadow-sm h-12 border border-grey-20 active:z-10',
        isPermanentColumn
          ? 'bg-grey-1 text-grey-60 border-grey-10'
          : 'bg-grey-10 text-grey-80 border border-grey-20',
        isReorderable
          ? 'cursor-grab active:cursor-grabbing active:shadow-lg transition-shadow'
          : 'cursor-auto active:cursor-auto',
      )}
    >
      {flattenedColumnOptions[column]?.translatedDisplayName ?? '-'}
      <div
        className={cn(
          'flex flex-row items-center gap-1',
          isPinnedColumn && isPermanentColumn ? 'fill-grey-60' : 'fill-white',
        )}
      >
        {!isPermanentColumn && (
          <button
            type="button"
            className={cn('cursor-pointer', isReorderable && 'z-10')}
            onClick={handleRemoveColumn}
            data-testid="column-visibility-menu-remove-column"
          >
            <X size={20} />
          </button>
        )}
        {isPinnedColumn ? (
          <Pin
            size={18}
            className={cn(
              'rotate-45',
              isPermanentColumn
                ? 'fill-grey-60 text-grey-60'
                : 'fill-grey-80 text-grey-80',
            )}
          />
        ) : (
          isReorderable && <GripVertical size={20} />
        )}
      </div>
    </Reorder.Item>
  );
};
